import { useMutation } from '@apollo/client/react'
import { graphql } from '../../../../gql'
import type {
  PaymentActionRequired,
  SubscriptionUpdateStatus,
} from '../../../../gql/graphql'
import { useOrganizationId } from '../../../../hooks/useCurrentOrganization'
import { QUERY_ACCOUNT } from '../../../../common/graphql/account'
import { ORGANIZATION_ACTIONS } from '../../../../common/events/orgEvents'
import { useAccount } from '../../../../hooks/useAccount'
import { prefixWithProduct } from '../../../../common/utils/prefixWithProduct'

const UpgradePlanToAnnualBillingMutation = graphql(/* GraphQL */ `
  mutation UpgradePlanToAnnualBilling(
    $input: UpgradePlanToAnnualBillingInput!
  ) {
    upgradePlanToAnnualBilling(input: $input) {
      __typename
      ... on UpgradePlanToAnnualBillingResponse {
        status
        paymentAction {
          clientSecret
          paymentMethodId
        }
      }
      ... on BillingError {
        userFriendlyMessage
        cause
      }
    }
  }
`)

type UpgradePlanResponse = {
  status: SubscriptionUpdateStatus
  paymentAction?: PaymentActionRequired | null | undefined
}

/**
 * Marketing attribution for the upgrade; `cta` is product-prefixed before it
 * is sent. Required so every annual-upgrade request carries attribution — a
 * frontend precursor to making it required on the API.
 */
type UpgradePlanAttribution = {
  cta: string
  upgradePathName: string
}

interface UseUpgradePlanToAnnualBillingReturn {
  upgradePlan: (
    attribution: UpgradePlanAttribution,
  ) => Promise<UpgradePlanResponse>
  loading: boolean
  error: Error | null | undefined
}

export function useUpgradePlanToAnnualBilling(): UseUpgradePlanToAnnualBillingReturn {
  const organizationId = useOrganizationId()
  const { account } = useAccount()

  const [executeMutation, { loading, error }] = useMutation(
    UpgradePlanToAnnualBillingMutation,
    {
      refetchQueries: [{ query: QUERY_ACCOUNT }, 'Invoices'],
      awaitRefetchQueries: true,
    },
  )

  const upgradePlan = async (
    attribution: UpgradePlanAttribution,
  ): Promise<UpgradePlanResponse> => {
    if (!organizationId) {
      throw new Error('Organization ID is required')
    }

    const result = await executeMutation({
      variables: {
        input: {
          organizationId,
          attribution: {
            cta: prefixWithProduct(attribution.cta),
            upgradePathName: attribution.upgradePathName,
          },
        },
      },
    })

    const response = result.data?.upgradePlanToAnnualBilling

    if (response?.__typename === 'BillingError') {
      throw new Error(response.userFriendlyMessage)
    }

    if (result.error) {
      throw new Error(result.error.message)
    }

    if (response?.__typename === 'UpgradePlanToAnnualBillingResponse') {
      ORGANIZATION_ACTIONS.billingUpdated({ account })
      return {
        status: response.status,
        paymentAction: response.paymentAction,
      }
    }

    throw new Error('Unexpected response from upgradePlanToAnnualBilling')
  }

  return {
    upgradePlan,
    loading,
    error,
  }
}
